import { createHttpHeader } from '@/utils/network/createHTTPHeaders';
import { getStaticDashboardConfig } from '@/hooks/useDashboardConfig';
import { TScaWalletFromApi, IScaWallet, TLockReason } from '@/store/models/sca.model';
import { decryptTreezorResponse } from '@/services/encryption/decryptTreezorResponse';
import { ApiScaWalletToScaWallet } from '@/services/utils';
import { fetchQuery } from '@/utils/queryClient';
import { ICursorElements } from '@/store/types/search.types';
import { fetchMethods } from '@/types/FetchService.types';

export class SCAService {
  static queryKey = 'SCA';
  static getWallets = async (userId: number, status: string, cursor?: string, staleTime?: number) => {
    try {
      const config = getStaticDashboardConfig();
      const headers = createHttpHeader();
      const cursorInfo = cursor ? `&cursor=${cursor}` : '';
      const url = `${config.api.baseUrl}/core-connect/sca/scawallets?userId=${userId}&status=${status}${cursorInfo}`;

      return await fetchQuery([`${this.queryKey}-${status}`, { method: fetchMethods.GET, user: userId }], url, {
        headers,
        staleTime,
      });
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static getSCAWallets = async (userId: number): Promise<IScaWallet[]> => {
    try {
      const activeWallets = await this.getWallets(userId, 'ACTIVE');
      const createdWallets = await this.getWallets(userId, 'CREATED');

      const scaWalletsFromApi = [
        ...(await decryptTreezorResponse<TScaWalletFromApi>(activeWallets, {
          shouldSkipEncryption: true,
        })),
        ...(await decryptTreezorResponse<TScaWalletFromApi>(createdWallets, {
          shouldSkipEncryption: true,
        })),
      ];

      return scaWalletsFromApi.map((scaWalletFromApi) => ApiScaWalletToScaWallet(scaWalletFromApi));
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static getDeletedSCAWallets = async (
    userId: number,
    cursor?: string,
    staleTime?: number,
  ): Promise<ICursorElements<IScaWallet>> => {
    try {
      const deletedWallets = await this.getWallets(userId, 'DELETED', cursor, staleTime);

      const scaWalletFromApi = await decryptTreezorResponse<TScaWalletFromApi>(deletedWallets, {
        shouldSkipEncryption: true,
        hasCursor: true,
      });

      return {
        ...scaWalletFromApi,
        elements: scaWalletFromApi.elements.map((scaWalletFromApi) => ApiScaWalletToScaWallet(scaWalletFromApi)),
      };
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static deleteSCAWallet = async (scaWalletId: string): Promise<IScaWallet> => {
    try {
      const config = getStaticDashboardConfig();

      const headers = createHttpHeader();
      const url = `${config.api.baseUrl}/core-connect/sca/scawallets/${scaWalletId}`;

      const response = await fetchQuery([this.queryKey, { method: fetchMethods.DELETE, id: scaWalletId }], url, {
        headers,
      });

      const scaWalletFromApi = await decryptTreezorResponse<TScaWalletFromApi>(response, {
        shouldSkipEncryption: true,
        isSingle: true,
      });

      return ApiScaWalletToScaWallet(scaWalletFromApi);
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static resetPIN = async (scaWalletId: string): Promise<IScaWallet> => {
    try {
      const config = getStaticDashboardConfig();

      const headers = createHttpHeader();
      const url = `${config.api.baseUrl}/core-connect/sca/scawallets/${scaWalletId}/resetPin`;

      const response = await fetchQuery(
        [this.queryKey, { method: fetchMethods.PUT, action: 'reset PIN', id: scaWalletId }],
        url,
        {
          headers,
        },
      );

      const scaWalletFromApi = await decryptTreezorResponse<TScaWalletFromApi>(response, {
        shouldSkipEncryption: true,
        isSingle: true,
      });

      return ApiScaWalletToScaWallet(scaWalletFromApi);
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static unlockSCAWallet = async (scaWalletId: string): Promise<IScaWallet> => {
    try {
      const config = getStaticDashboardConfig();

      const headers = createHttpHeader();
      const url = `${config.api.baseUrl}/core-connect/sca/scawallets/${scaWalletId}/unlock`;

      const response = await fetchQuery(
        [this.queryKey, { method: fetchMethods.PUT, action: 'unlock', id: scaWalletId }],
        url,
        {
          headers,
        },
      );

      const scaWalletFromApi = await decryptTreezorResponse<TScaWalletFromApi>(response, { isSingle: true });

      return ApiScaWalletToScaWallet(scaWalletFromApi);
    } catch (error) {
      return Promise.reject(error);
    }
  };

  static secureSCAWallet = async (scaWalletId: string, lockReason: TLockReason, lockMessage?: string) => {
    try {
      const config = getStaticDashboardConfig();

      const headers = createHttpHeader();
      const url = `${config.api.baseUrl}/core-connect/sca/scawallets/${scaWalletId}/lock`;

      const response = await fetchQuery(
        [
          this.queryKey,
          { method: fetchMethods.PUT, action: 'secure', id: scaWalletId, params: { lockReason, lockMessage } },
        ],
        url,
        {
          headers,
          body: JSON.stringify({ lockReason, lockMessage }),
        },
      );

      const scaWalletFromApi = await decryptTreezorResponse<TScaWalletFromApi>(response, {
        shouldSkipEncryption: true,
        isSingle: true,
      });

      return ApiScaWalletToScaWallet(scaWalletFromApi);
    } catch (err) {
      return Promise.reject(err);
    }
  };
}
